export const TREE_SIZE = 8;
export const BYE_FILL_SIZE = 7;
export const PRODUCT_KIND_TRADITIONAL = "traditional";
export const SCORING_SCRATCH = "scratch";
export const SCORING_HANDICAP = "handicap";
export const TIE_RULE_USBC_WC = "usbc_wc";
export const DEFAULT_GAME_ORDER = [1, 2, 3];

export const PAYOUT_FIRST = "first";
export const PAYOUT_SECOND = "second";
export const PAYOUT_REFUND = "refund";
export const PAYOUT_CHOP = "chop";

export const suggestedPayouts = (entryAmount, houseCut) => {
  const entry = Number(entryAmount) || 0;
  const cut = Number(houseCut) || 0;
  const pot = Math.max(0, roundMoney(entry * TREE_SIZE - cut));
  const second = Math.min(roundMoney(entry * 2), pot);
  const first = roundMoney(pot - second);
  return { firstPlaceAmount: first, secondPlaceAmount: second };
};

export const defaultProductFields = () => {
  const entryAmount = 5;
  const houseCut = 5;
  const payouts = suggestedPayouts(entryAmount, houseCut);
  return {
    name: "Scratch $5",
    productKind: PRODUCT_KIND_TRADITIONAL,
    scoringMode: SCORING_SCRATCH,
    gameOrder: [...DEFAULT_GAME_ORDER],
    treeSize: TREE_SIZE,
    entryAmount,
    houseCut,
    ...payouts,
    tieRule: TIE_RULE_USBC_WC,
    sourceEventID: "",
  };
};

export const roundMoney = (value) =>
  Math.round((Number(value) + Number.EPSILON) * 100) / 100;

export const productLabel = (product) => {
  if (!product) return "Brackets";
  if (product.name) return product.name;
  const mode = product.scoringMode === SCORING_HANDICAP ? "Handicap" : "Scratch";
  return `${mode} $${Number(product.entryAmount) || 0}`;
};

export const planShuffle = (ticketCount) => {
  const count = Math.max(0, Number(ticketCount) || 0);
  const treesOf8 = Math.floor(count / TREE_SIZE);
  const remainder = count % TREE_SIZE;
  const treeOf7 = remainder >= BYE_FILL_SIZE ? 1 : 0;
  const leftover = treeOf7 ? remainder - BYE_FILL_SIZE : remainder;
  return {
    ticketCount: count,
    treesOf8,
    treeOf7,
    leftover,
    canShuffle: treesOf8 + treeOf7 > 0,
  };
};

const shuffleList = (items, random) => {
  const next = [...items];
  for (let i = next.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    [next[i], next[j]] = [next[j], next[i]];
  }
  return next;
};

const takeUniqueBowlers = (pool, size) => {
  const taken = [];
  const used = new Set();
  const rest = [];
  pool.forEach((ticket) => {
    if (taken.length < size && !used.has(ticket.bowlerID)) {
      taken.push(ticket);
      used.add(ticket.bowlerID);
    } else {
      rest.push(ticket);
    }
  });
  if (taken.length < size) return null;
  return { taken, rest };
};

export const planDeal = (tickets) => {
  const list = tickets || [];
  const countByBowler = new Map();
  list.forEach((ticket) => {
    countByBowler.set(
      ticket.bowlerID,
      (countByBowler.get(ticket.bowlerID) || 0) + 1
    );
  });
  const uniqueBowlers = countByBowler.size;
  let remaining = [...countByBowler.values()];
  let treesOf8 = 0;
  let treeOf7 = 0;

  const take = (size) => {
    remaining.sort((a, b) => b - a);
    if (remaining.filter((count) => count > 0).length < size) return false;
    for (let i = 0; i < size; i += 1) {
      remaining[i] -= 1;
    }
    remaining = remaining.filter((count) => count > 0);
    return true;
  };

  while (take(TREE_SIZE)) {
    treesOf8 += 1;
  }
  if (take(BYE_FILL_SIZE)) {
    treeOf7 = 1;
  }

  return {
    ticketCount: list.length,
    uniqueBowlers,
    treesOf8,
    treeOf7,
    leftover: remaining.reduce((sum, count) => sum + count, 0),
    canShuffle: treesOf8 + treeOf7 > 0,
  };
};

export const dealTrees = (tickets, options = {}) => {
  const random = options.random || Math.random;
  let pool =
    options.shuffle === false
      ? [...(tickets || [])]
      : shuffleList(tickets || [], random);
  const trees = [];
  while (true) {
    const eight = takeUniqueBowlers(pool, TREE_SIZE);
    if (!eight) break;
    trees.push({ tickets: eight.taken, hasBye: false });
    pool = eight.rest;
  }
  const seven = takeUniqueBowlers(pool, BYE_FILL_SIZE);
  if (seven) {
    trees.push({ tickets: seven.taken, hasBye: true });
    pool = seven.rest;
  }
  return { trees, leftover: pool };
};

export const shuffleSummary = (plan) => {
  if (!plan?.canShuffle) {
    if (
      plan?.uniqueBowlers != null &&
      plan.uniqueBowlers < BYE_FILL_SIZE
    ) {
      return "Need 7 different bowlers to fill a tree.";
    }
    return "Need at least 7 tickets to shuffle.";
  }
  const parts = [];
  if (plan.treesOf8) {
    parts.push(
      plan.treesOf8 === 1
        ? "1 tree of 8"
        : `${plan.treesOf8} trees of 8`
    );
  }
  if (plan.treeOf7) {
    parts.push("1 tree of 7 with a bye");
  }
  if (plan.leftover) {
    parts.push(
      plan.leftover === 1
        ? "1 leftover ticket — refund"
        : `${plan.leftover} leftover tickets — refund`
    );
  }
  if (!parts.length) {
    return "Need at least 7 tickets to shuffle.";
  }
  return parts.join(". ") + ".";
};

export const summarizeBracketSquads = ({
  squads,
  teams,
  tickets,
  products,
}) => {
  const bySquad = new Map();
  (squads || []).forEach((squad) => {
    bySquad.set(squad.id, {
      squadId: squad.id,
      bowlerIds: new Set(),
      teamCount: 0,
      ticketCount: 0,
      ticketsByProduct: {},
      shuffled: false,
    });
  });
  (products || []).forEach((product) => {
    (product.bracketProductSquad || []).forEach((row) => {
      const summary = bySquad.get(row.squadID);
      if (summary && row.shuffledAt) summary.shuffled = true;
    });
  });
  (teams || []).forEach((team) => {
    const summary = bySquad.get(team.squadID);
    if (!summary || team.isDeleted) return;
    summary.teamCount += 1;
    (team.teamassignments || []).forEach((assignment) => {
      const bowlerId = assignment.bowlerID || assignment.bowlers?.id;
      if (bowlerId) summary.bowlerIds.add(bowlerId);
    });
  });
  (tickets || []).forEach((ticket) => {
    const summary = bySquad.get(ticket.squadID);
    if (!summary) return;
    summary.ticketCount += 1;
    const productId = ticket.productID;
    summary.ticketsByProduct[productId] =
      (summary.ticketsByProduct[productId] || 0) + 1;
  });
  return (squads || []).map((squad) => {
    const summary = bySquad.get(squad.id);
    return {
      squad,
      bowlerCount: summary?.bowlerIds.size || 0,
      teamCount: summary?.teamCount || 0,
      ticketCount: summary?.ticketCount || 0,
      ticketsByProduct: summary?.ticketsByProduct || {},
      shuffled: Boolean(summary?.shuffled),
    };
  });
};

